import { useState, type FormEvent } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { supabase, supabaseConfigured } from '../lib/supabase.ts';
import { useAuth } from '../lib/auth.tsx';
import Button from '../components/Button.tsx';
import { Field, TextInput } from '../components/Field.tsx';

export default function LoginPage() {
  const { user, configured } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mode, setMode] = useState<'password' | 'magic'>('password');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [pending, setPending] = useState(false);

  if (user) return <Navigate to="/app" replace />;

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError('');
    setMessage('');
    if (!supabase) {
      setError('Supabase is not configured in this environment.');
      return;
    }
    if (mode === 'magic') {
      setError('Magic link is not available until custom SMTP is set. Use a password.');
      return;
    }
    if (!password) {
      setError('Enter the password for this account.');
      return;
    }
    setPending(true);
    try {
      const { error: signError } = await supabase.auth.signInWithPassword({ email, password });
      if (signError) throw signError;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not sign in.');
    } finally {
      setPending(false);
    }
  }

  async function onSignUp() {
    setError('');
    setMessage('');
    if (!supabase) {
      setError('Supabase is not configured in this environment.');
      return;
    }
    if (!password) {
      setError('Choose a password to create an account.');
      return;
    }
    setPending(true);
    try {
      const { error: signError } = await supabase.auth.signUp({
        email,
        password,
      });
      if (signError) throw signError;
      setMessage(
        'Account created. Sign in with the password you just chose. Confirmation mail will not arrive until custom SMTP is set.',
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not create that account.');
    } finally {
      setPending(false);
    }
  }

  return (
    <main className="mx-auto flex min-h-screen max-w-md flex-col justify-center px-6 py-16">
      <p className="text-sm uppercase tracking-widest text-ink-subtle">Austin OS</p>
      <h1 className="mt-2 text-3xl">Sign in</h1>
      <p className="mt-2 text-ink-muted">
        Password is the door until custom SMTP is set. Magic link will not arrive — this project
        does not send mail yet.
      </p>

      {!configured ? (
        <p className="mt-6 rounded border border-border bg-surface p-4 text-ink-muted">
          This build has no Supabase keys. Copy <code className="font-mono text-sm">.env.example</code>{' '}
          to <code className="font-mono text-sm">.env</code> and restart.
        </p>
      ) : mode === 'magic' ? (
        <div className="mt-8 space-y-4">
          <p className="rounded border border-border bg-surface p-4 text-ink-muted">
            Magic link is not available yet. A sign-in email will not be sent. Use a password.
          </p>
          <Button tone="secondary" onClick={() => setMode('password')}>
            Use a password
          </Button>
        </div>
      ) : (
        <form className="mt-8 space-y-4" onSubmit={(event) => void onSubmit(event)}>
          <Field label="Email">
            <TextInput
              type="email"
              name="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </Field>
          <Field label="Password">
            <TextInput
              type="password"
              name="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </Field>
          {error ? <p className="text-sm text-danger">{error}</p> : null}
          {message ? <p className="text-sm text-ink-muted">{message}</p> : null}
          <div className="flex flex-wrap items-center gap-3">
            <Button type="submit" disabled={pending || !supabaseConfigured}>
              Sign in
            </Button>
            <Button tone="secondary" disabled={pending} onClick={() => void onSignUp()}>
              Create account
            </Button>
            <Button tone="quiet" onClick={() => setMode('magic')}>
              Why no magic link?
            </Button>
          </div>
        </form>
      )}

      <p className="mt-10 text-sm">
        <Link to="/" className="text-accent-600 underline underline-offset-4">
          Back to the start
        </Link>
      </p>
    </main>
  );
}
